import React, { useState, useEffect, useRef } from "react";
import logo from "../image/logo.png";
import "./LoginAnimation.css";

const STEPS = [
  { id: "01", label: "Inward" },
  { id: "02", label: "Job Assign" },
  { id: "03", label: "Calibration In Progress" },
  { id: "04", label: "Send For Approval" },
  { id: "05", label: "Approved Certificate" },
  { id: "06", label: "Dispatch" },
];

const LoginAnimation = ({ onFinish }) => {
  const [stage, setStage] = useState(1);

  // closing phase flags — each triggers a @keyframes animation class
  const [workflowExit, setWorkflowExit] = useState(false); // flowchart + "Automate" fade out
  const [logoMeeting, setLogoMeeting] = useState(false);   // logo slides down
  const [textMeeting, setTextMeeting] = useState(false);   // tagline slides up (slight delay)
  const [collapseOut, setCollapseOut] = useState(false);   // both fade-rise out together

  const finishedRef = useRef(false);

  const completeTransition = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (typeof onFinish === "function") onFinish();
  };

  useEffect(() => {
    // Stage 1 → Stage 2 (flowchart)
    const t1 = setTimeout(() => setStage(2), 2100);

    // SIMULTANEOUSLY: flowchart fades out + logo starts sliding down
    const t2 = setTimeout(() => {
      setWorkflowExit(true);
      setLogoMeeting(true);
    }, 4200);

    // Tagline slides up — 350ms after logo starts coming down
    const t3 = setTimeout(() => setTextMeeting(true), 4550);

    // Both collapse/fade out together — after ~1.5s of meeting
    const t4 = setTimeout(() => setCollapseOut(true), 6000);

    // Navigate to dashboard
    const t5 = setTimeout(() => completeTransition(), 6600);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, []);

  // Compute logo class — only ONE animation is active at a time
  const logoClass = (() => {
    if (collapseOut) return "login-anim-logo-card logo-anim-collapse";
    if (logoMeeting) return "login-anim-logo-card logo-anim-meet";
    return "login-anim-logo-card logo-anim-enter";
  })();

  // Compute tagline class
  const taglineClass = (() => {
    if (collapseOut) return "login-anim-footer-sub tagline-anim-collapse";
    if (textMeeting) return "login-anim-footer-sub tagline-anim-meet";
    return "login-anim-footer-sub";
  })();

  return (
    <div className="login-anim-overlay">
      {/* Ambient Orbs */}
      <div className="login-anim-orbs-container">
        <div className="login-anim-orb orb-tr" />
        <div className="login-anim-orb orb-tl" />
        <div className="login-anim-orb orb-center" />
        <div className="login-anim-orb orb-bl" />
      </div>

      {/* Wave */}
      <div className="login-anim-wave-wrapper">
        <svg className="login-anim-wave-svg" viewBox="0 0 1440 60" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 30 Q 360 8 720 30 T 1440 30" stroke="rgba(191, 219, 254, 0.42)" strokeWidth="1.5" fill="none" />
        </svg>
      </div>

      {/* Main Content */}
      <div className="login-anim-content">

        {/* Logo — always in DOM, animation class switches drive movement */}
        <div className={logoClass}>
          <img src={logo} alt="BFI LIMS Logo" className="login-anim-logo-img" />
        </div>

        {/* Dynamic Stage */}
        <div className="login-anim-dynamic-stage">

          {stage === 1 && (
            <div className="login-anim-welcome-stage">
              <h1 className="login-anim-title">Welcome to BFI LIMS</h1>
              <p className="login-anim-subtitle">Smarter Calibration. Stronger Compliance.</p>
            </div>
          )}

          {stage === 2 && (
            <div className="login-anim-workflow-stage">

              {/* Flowchart cards */}
              <div className={`login-anim-cards-track${workflowExit ? " workflow-fade-exit" : ""}`}>
                {STEPS.map((step, idx) => (
                  <div
                    key={step.id}
                    className="login-anim-card-item"
                    style={{ animationDelay: `${idx * 0.08}s` }}
                  >
                    <div className="login-anim-card">
                      <span className="login-anim-badge">{step.id}</span>
                      <span className="login-anim-card-label">{step.label}</span>
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className="login-anim-arrow">
                        <svg className="login-anim-arrow-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="login-anim-workflow-footer">
                {/* "Automate..." headline — fades with flowchart */}
                <h2 className={`login-anim-footer-headline${workflowExit ? " workflow-fade-exit" : ""}`}>
                  Automate. Validate. Deliver.
                </h2>

                {/* "Advanced LIMS..." — slides up to meet logo */}
                <p className={taglineClass}>
                  Advanced LIMS for modern calibration laboratories
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default LoginAnimation;
